import React, { useEffect, useRef } from 'react';
import Header from '../components/Header';
import './SchoolStaff.css';

const DATA = [
  { town: "Brookline",  pop: 63266, totalFte: 1264.1, teacherFte: 630.7, students: 6948 },
  { town: "Newton",     pop: 89044, totalFte: 2157.5, teacherFte: 1086.0, students: 11461 },
  { town: "Lexington",  pop: 34295, totalFte: 1209.9, teacherFte: 634.1, students: 6524 },
  { town: "Arlington",  pop: 46350, totalFte: 938.8,  teacherFte: 503.6, students: 6098 },
  { town: "Natick",     pop: 36873, totalFte: 827.6,  teacherFte: 437.9, students: 5223 },
  { town: "Watertown",  pop: 35543, totalFte: 557.0,  teacherFte: 273.9, students: 2843 },
  { town: "Needham",    pop: 32459, totalFte: 895.1,  teacherFte: 458.0, students: 5427 },
  { town: "Wellesley",  pop: 30347, totalFte: 789.4,  teacherFte: 374.2, students: 3922 },
  { town: "Milton",     pop: 28587, totalFte: 555.9,  teacherFte: 335.1, students: 4358 },
  { town: "Belmont",    pop: 27175, totalFte: 539.2,  teacherFte: 295.7, students: 4433 },
].sort((a, b) => b.pop - a.pop).map(d => ({
  ...d,
  otherFte: +(d.totalFte - d.teacherFte).toFixed(1),
  staffPer1k: +(d.totalFte / d.pop * 1000).toFixed(2),
  studentsPerStaff: +(d.students / d.totalFte).toFixed(2),
  studentsPerTeacher: +(d.students / d.teacherFte).toFixed(2),
}));

const BROOKLINE_COLOR = '#2B6CB0';
const OTHER_COLOR = '#90CDF4';
const POP_COLOR = '#E2E8F0';

function SchoolStaff() {
  const dualRef = useRef(null);
  const ratioRef = useRef(null);
  const teacherStudentRef = useRef(null);
  const staffStudentRef = useRef(null);
  const compRef = useRef(null);
  const chartsRef = useRef([]);

  useEffect(() => {
    document.title = 'School Staff vs. Population — Brookline';
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadChartJs = async () => {
      if (!window.Chart) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      if (cancelled) return;
      renderCharts();
    };

    loadChartJs();

    return () => {
      cancelled = true;
      chartsRef.current.forEach(c => c.destroy());
      chartsRef.current = [];
    };
  }, []);

  const renderCharts = () => {
    const Chart = window.Chart;
    const colors = DATA.map(d => d.town === "Brookline" ? BROOKLINE_COLOR : OTHER_COLOR);
    const popColors = DATA.map(d => d.town === "Brookline" ? '#A0AEC0' : POP_COLOR);

    // Chart 1: Dual-axis staff vs population
    chartsRef.current.push(new Chart(dualRef.current.getContext('2d'), {
      type: 'bar',
      data: {
        labels: DATA.map(d => d.town),
        datasets: [
          {
            label: 'Population',
            data: DATA.map(d => d.pop),
            backgroundColor: popColors,
            borderRadius: 4,
            yAxisID: 'yPop',
            order: 2
          },
          {
            label: 'Total Staff FTE',
            data: DATA.map(d => d.totalFte),
            backgroundColor: colors,
            borderRadius: 4,
            yAxisID: 'yFTE',
            order: 1
          }
        ]
      },
      options: {
        responsive: true,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          tooltip: { callbacks: { label: ctx => ctx.dataset.label + ': ' + ctx.raw.toLocaleString() } },
          legend: { position: 'top' }
        },
        scales: {
          yPop: { type: 'linear', position: 'left', title: { display: true, text: 'Population' }, grid: { drawOnChartArea: true }, ticks: { callback: v => v.toLocaleString() } },
          yFTE: { type: 'linear', position: 'right', title: { display: true, text: 'Total Staff FTE' }, grid: { drawOnChartArea: false } }
        }
      }
    }));

    // Chart 2: Staff per 1,000 residents
    const ratioSorted = [...DATA].sort((a, b) => b.staffPer1k - a.staffPer1k);
    chartsRef.current.push(new Chart(ratioRef.current.getContext('2d'), {
      type: 'bar',
      data: {
        labels: ratioSorted.map(d => d.town),
        datasets: [{
          label: 'Staff per 1,000 residents',
          data: ratioSorted.map(d => d.staffPer1k),
          backgroundColor: ratioSorted.map(d => d.town === "Brookline" ? BROOKLINE_COLOR : OTHER_COLOR),
          borderRadius: 4
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: ctx => ctx.raw + ' school staff per 1,000 residents' } }
        },
        scales: { x: { title: { display: true, text: 'Staff per 1,000 Residents' }, beginAtZero: true } }
      }
    }));

    // Chart 3: Students per teacher FTE
    const tsSorted = [...DATA].sort((a, b) => a.studentsPerTeacher - b.studentsPerTeacher);
    chartsRef.current.push(new Chart(teacherStudentRef.current.getContext('2d'), {
      type: 'bar',
      data: {
        labels: tsSorted.map(d => d.town),
        datasets: [{
          label: 'Students per teacher FTE',
          data: tsSorted.map(d => d.studentsPerTeacher),
          backgroundColor: tsSorted.map(d => d.town === "Brookline" ? BROOKLINE_COLOR : OTHER_COLOR),
          borderRadius: 4
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: ctx => {
                const d = tsSorted[ctx.dataIndex];
                return ctx.raw + ' students per teacher FTE  (' + d.students.toLocaleString() + ' students / ' + d.teacherFte.toLocaleString() + ' teachers)';
              }
            }
          }
        },
        scales: { x: { title: { display: true, text: 'Students per Teacher FTE (lower = smaller class sizes)' }, beginAtZero: true } }
      }
    }));

    // Chart 4: Students per staff FTE
    const ssSorted = [...DATA].sort((a, b) => a.studentsPerStaff - b.studentsPerStaff);
    chartsRef.current.push(new Chart(staffStudentRef.current.getContext('2d'), {
      type: 'bar',
      data: {
        labels: ssSorted.map(d => d.town),
        datasets: [{
          label: 'Students per staff FTE',
          data: ssSorted.map(d => d.studentsPerStaff),
          backgroundColor: ssSorted.map(d => d.town === "Brookline" ? BROOKLINE_COLOR : OTHER_COLOR),
          borderRadius: 4
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: ctx => {
                const d = ssSorted[ctx.dataIndex];
                return ctx.raw + ' students per staff FTE  (' + d.students.toLocaleString() + ' students / ' + d.totalFte.toLocaleString() + ' staff)';
              }
            }
          }
        },
        scales: { x: { title: { display: true, text: 'Students per Staff FTE (lower = more staff per student)' }, beginAtZero: true } }
      }
    }));

    // Chart 5: Stacked composition
    const compData = [...DATA].sort((a, b) => b.totalFte - a.totalFte);
    chartsRef.current.push(new Chart(compRef.current.getContext('2d'), {
      type: 'bar',
      data: {
        labels: compData.map(d => d.town),
        datasets: [
          {
            label: 'Teacher FTE',
            data: compData.map(d => d.teacherFte),
            backgroundColor: compData.map(d => d.town === "Brookline" ? '#2B6CB0' : '#63B3ED'),
            borderRadius: 0
          },
          {
            label: 'Other Staff FTE',
            data: compData.map(d => d.otherFte),
            backgroundColor: compData.map(d => d.town === "Brookline" ? '#2C5282' : '#BEE3F8'),
            borderRadius: 4
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { position: 'top' },
          tooltip: {
            callbacks: {
              afterBody: (items) => {
                const d = compData[items[0].dataIndex];
                return 'Teachers: ' + ((d.teacherFte / d.totalFte) * 100).toFixed(1) + '% of total staff';
              }
            }
          }
        },
        scales: {
          x: { stacked: true },
          y: { stacked: true, title: { display: true, text: 'Staff FTE' }, ticks: { callback: v => v.toLocaleString() } }
        }
      }
    }));
  };

  const tableData = [...DATA].sort((a, b) => a.studentsPerStaff - b.studentsPerStaff);

  return (
    <div className="school-staff-page">
      <Header
        title="School Staff vs. Population"
        subtitle="Brookline & Comparable MA Communities"
      />

      <div className="school-staff-content">
        <p className="ss-subtitle">DESE 2025-26 All-Staff FTE &amp; Enrollment data &bull; 2024 ACS population estimates</p>

        <div className="charts-grid">
          <div className="card">
            <h2>Total Staff FTE and Town Population</h2>
            <div className="chart-wrap"><canvas ref={dualRef} height="340"></canvas></div>
          </div>

          <div className="card">
            <h2>School Department Staff per 1,000 Residents</h2>
            <div className="chart-wrap"><canvas ref={ratioRef} height="300"></canvas></div>
          </div>

          <div className="card">
            <h2>Teacher-to-Student Ratio (students per teacher FTE)</h2>
            <div className="chart-wrap"><canvas ref={teacherStudentRef} height="300"></canvas></div>
          </div>

          <div className="card">
            <h2>Overall Staff-to-Student Ratio (students per staff FTE)</h2>
            <div className="chart-wrap"><canvas ref={staffStudentRef} height="300"></canvas></div>
          </div>

          <div className="card">
            <h2>Staff Composition: Teachers vs. Other Staff</h2>
            <div className="chart-wrap"><canvas ref={compRef} height="300"></canvas></div>
          </div>

          <div className="card">
            <h2>Underlying Data</h2>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Community</th>
                    <th>Population (2024)</th>
                    <th>Students (2025-26)</th>
                    <th>Total Staff FTE</th>
                    <th>Teacher FTE</th>
                    <th>Other Staff FTE</th>
                    <th>Staff per 1,000 Residents</th>
                    <th>Students per Teacher FTE</th>
                    <th>Students per Staff FTE</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map(d => (
                    <tr key={d.town} className={d.town === 'Brookline' ? 'highlight' : ''}>
                      <td>{d.town}</td>
                      <td>{d.pop.toLocaleString()}</td>
                      <td>{d.students.toLocaleString()}</td>
                      <td>{d.totalFte.toLocaleString()}</td>
                      <td>{d.teacherFte.toLocaleString()}</td>
                      <td>{d.otherFte.toLocaleString()}</td>
                      <td>{d.staffPer1k}</td>
                      <td>{d.studentsPerTeacher}</td>
                      <td>{d.studentsPerStaff}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="note">
              <strong>What counts as "staff"?</strong> Total Staff FTE includes all school department employees reported to DESE: teachers, administrators, paraprofessionals, counselors, librarians, nurses, office/clerical staff, special education support, and other instructional and non-instructional roles. A lower students-per-staff ratio means more staff resources per student.
            </div>
          </div>
        </div>

        <p className="source">
          <strong>Sources:</strong>{' '}
          Total Staff FTE from <a href="https://profiles.doe.mass.edu/statereport/teacherbyracegender.aspx" target="_blank" rel="noopener noreferrer">MA DESE Staffing Data Report</a> (2025-26, All Classifications).{' '}
          Teacher FTE from <a href="https://profiles.doe.mass.edu/statereport/teacherdata.aspx" target="_blank" rel="noopener noreferrer">MA DESE Teacher Data Report</a> (2025-26).{' '}
          Student enrollment from <a href="https://profiles.doe.mass.edu/statereport/enrollmentbygrade.aspx" target="_blank" rel="noopener noreferrer">MA DESE Enrollment by Grade Report</a> (2025-26).{' '}
          Population from <a href="https://www.massachusetts-demographics.com/cities_by_population" target="_blank" rel="noopener noreferrer">2024 American Community Survey</a>.
        </p>
      </div>
    </div>
  );
}

export default SchoolStaff;
